'use client'

import * as React from 'react'
import {
  getCountries,
  getCountryCallingCode,
  AsYouType,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import type { CountryCode } from 'libphonenumber-js'
import { cn } from '@/lib/utils'
import { InputGroup, InputGroupInput } from './input-group'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from './combobox'

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' })

function getFlag(code: string) {
  return [...code.toUpperCase()]
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('')
}

interface CountryItem {
  code: CountryCode
  value: string // shown in input when selected: "🇳🇴 +47"
  name: string // full country name for searching
  flag: string
  callingCode: string
}

const PRIORITY_COUNTRIES: CountryCode[] = ['SE', 'NO', 'DK']

const ALL_COUNTRIES: CountryItem[] = getCountries()
  .map((code) => {
    const name = regionNames.of(code) ?? code
    const callingCode = `+${getCountryCallingCode(code)}`
    return {
      code,
      value: `${getFlag(code)} ${callingCode}`,
      name,
      flag: getFlag(code),
      callingCode,
    }
  })
  .sort((a, b) => {
    const aPriority = PRIORITY_COUNTRIES.indexOf(a.code)
    const bPriority = PRIORITY_COUNTRIES.indexOf(b.code)
    if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority
    if (aPriority !== -1) return -1
    if (bPriority !== -1) return 1
    return a.name.localeCompare(b.name)
  })

type PhoneInputProps = Omit<React.ComponentProps<'input'>, 'type' | 'placeholder' | 'onChange'> & {
  onChange?: (value: string) => void
  countryCode?: string
}

function parseInitialValue(value?: React.ComponentProps<'input'>['value']) {
  if (!value || typeof value !== 'string') return { country: null, phone: '' }
  try {
    const parsed = parsePhoneNumberFromString(`+${value}`)
    if (!parsed) return { country: null, phone: value }
    const country = ALL_COUNTRIES.find((c) => c.code === parsed.country) ?? null
    return { country, phone: parsed.formatNational() }
  } catch {
    return { country: null, phone: value }
  }
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { onChange, onBlur, name, className, disabled, value, countryCode, ...props },
  ref,
) {
  const [selectedCountry, setSelectedCountry] = React.useState<CountryItem | null>(
    () => parseInitialValue(value).country,
  )

  const [phone, setPhone] = React.useState(() => parseInitialValue(value).phone)

  React.useEffect(() => {
    if (!countryCode) return
    const match = ALL_COUNTRIES.find((c) => c.code === (countryCode.toUpperCase() as CountryCode))
    if (match && match.code !== selectedCountry?.code) {
      setSelectedCountry(match)
      setPhone('')
    }
  }, [countryCode])

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    const formatted = digits ? new AsYouType(selectedCountry?.code).input(digits) : ''
    setPhone(formatted)
    const callingCode = selectedCountry?.callingCode.replace('+', '') ?? ''
    const fullValue = callingCode + digits

    onChange?.(fullValue)
  }

  return (
    <>
      <input
        ref={ref}
        type='hidden'
        name={name}
        onBlur={onBlur}
        value={value}
        readOnly
        {...props}
      />
      <InputGroup className={cn('border-secondary bg-input h-10 shadow-sm', className)}>
        <Combobox
          items={ALL_COUNTRIES}
          itemToStringLabel={(item: CountryItem) => item.callingCode}
          filter={(item: CountryItem, query: string) =>
            item.name.toLowerCase().includes(query.toLowerCase()) ||
            item.callingCode.includes(query)
          }
          isItemEqualToValue={(a: CountryItem, b: CountryItem) => a.value === b.value}
          value={selectedCountry}
          onValueChange={(value: CountryItem | null) => {
            setSelectedCountry(value)
            setPhone('')
          }}
        >
          <ComboboxInput
            className='w-24 rounded-none border-0 bg-transparent shadow-none has-[[data-slot=input-group-control]:focus-visible]:border-transparent has-[[data-slot=input-group-control]:focus-visible]:ring-0 dark:bg-transparent'
            placeholder='Prefix'
          />
          <ComboboxContent className='min-w-64'>
            <ComboboxEmpty>No countries found.</ComboboxEmpty>
            <ComboboxList>
              {(item: CountryItem) => (
                <ComboboxItem key={item.value} value={item}>
                  <span>{item.flag}</span>
                  <span>{item.name}</span>
                  <span className='text-muted-foreground ml-auto'>{item.callingCode}</span>
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
        <div className='bg-border my-1 w-px shrink-0 self-stretch' />
        <InputGroupInput
          type='tel'
          value={phone}
          onChange={handlePhoneChange}
          onBlur={onBlur}
          placeholder='Phone number'
          disabled={disabled}
        />
      </InputGroup>
    </>
  )
})

export { PhoneInput }
